<!-- eslint-disable tailwindcss/no-custom-classname -->
<template>
  <div
    class="relative flex flex-col"
    :class="colSpan"
  >
    <h3
      v-if="field?.display"
      class="w-full typo-subtitle"
    >
      {{ field.display }}
    </h3>
    <p v-if="instructions || field.instructions" class="mt-1">
      {{ instructions || field.instructions }}
    </p>
    <ul
      class="flex flex-1 flex-col flex-wrap gap-3"
      :class="field?.display || instructions || field.instructions ? 'mt-1': 'mt-5'"
    >
      <li v-for="(option) in computedOptions" :key="option.label" class="group w-fit">
        <label
          class="group relative flex h-8 w-fit cursor-pointer select-none items-center pl-11"
          :for="option.value"
        >
          <span class="block !leading-none typo-subtitle">
            {{ option.label }}
          </span>

          <Field
            :id="option.value"
            :value="option.value"
            :name="name || handle || field.handle"
            :label="label || field.label || field.display"
            :disabled="disabled"
            :rules="rulesAsString"
            type="radio"
            class="peer absolute right-0 z-0 cursor-pointer opacity-0"
          />

          <span class="radio-button" />
        </label>
      </li>
    </ul>

    <!-- ERROR -->
    <FieldError :error="error" />
  </div>
</template>

<!-- eslint-disable-next-line vue/block-lang -->
<script setup>
import { Field } from 'vee-validate'

defineEmits(['update:modelValue'])

const props = defineProps({
  options: {
    type: [Array, Object],
    default: () => [],
  },

  handle: {
    type: String,
    default: null,
  },

  field: {
    type: Object,
    default: null,
  },

  width: {
    type: [Number, String],
    default: null,
  },

  rules: {
    type: [Array, String],
    default: '',
  },

  disabled: {
    type: Boolean,
    default: false,
  },

  label: {
    type: String,
    default: '',
  },

  name: {
    type: String,
    default: null,
  },

  instructions: {
    type: String,
    default: null,
  },

  error: {
    type: String,
    default: null,
  },
})

const rulesAsString = useFilterVeeValidateRules(props.rules)

const colSpan = computed(() => {
  const width = parseInt(props.width) || props.field?.width || 100

  switch (width) {
    case 25:
      return 'lg:col-span-3 col-span-12'
    case 33:
      return 'lg:col-span-4 col-span-12'
    case 50:
      return 'lg:col-span-6 col-span-12'
    case 66:
      return 'lg:col-span-8 col-span-12'
    default:
      return 'lg:col-span-12 col-span-12'
  }
})

const computedOptions = computed(() => {
  if (Array.isArray(props.options)) {
    return props.options.map((option) => {
      if (typeof option === 'string') {
        return {
          label: option,
          value: option,
        }
      }

      return {
        label: option.value,
        value: option.key,
      }
    })
  }

  return Object.keys(props.options).map(key => ({
    label: props.options[key],
    value: key,
  }))
})
</script>

<style scoped>
.radio-button {
  @apply absolute inset-x-0 top-0 ml-px block size-8 overflow-hidden rounded-full border-2  border-primary-900 transition-all duration-200 ease-in-out;

  @apply peer-focus-visible:ring peer-focus-visible:ring-offset-2;

  @apply group-hover:border-secondary-900;

  @apply group-hover:peer-checked:border-primary-900;

  @apply peer-checked:border-[3px]  ;

  @apply
    before:absolute
    before:inset-0
    before:scale-0
    before:rounded-full
    before:bg-primary-900
    before:opacity-0
    before:transition-all
    before:duration-150;

  @apply
    peer-checked:before:scale-90
    peer-checked:before:opacity-100
    peer-checked:before:duration-200
    peer-checked:before:ease-out;

  @apply
    after:absolute
    after:inset-0
    after:scale-0
    after:rounded-full
    after:bg-gradient-01
    after:opacity-0
    after:transition-all
    after:duration-500;

  @apply
    group-hover:peer-checked:after:scale-90
    group-hover:peer-checked:after:opacity-100
    group-hover:peer-checked:after:ease-out;

}
</style>
